import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSpring, useTransition, animated } from "@react-spring/web";
import {
  Tabs, TabsList, TabsTrigger,
  Dialog, DialogContent, DialogHeader, DialogMain,
  DialogTitle, DialogDescription, DialogFooter,
} from "@ncoder/ui";
import {
  FileCode, FileText, Zap, Workflow, Database, Layout, Archive,
  Loader2, AlertCircle, Upload, GitMerge, Trash2, Github,
  Cloud, CheckCircle2, XCircle, GitBranch, ChevronRight, ChevronDown, Plus, Minus, Check,
} from "lucide-react";
import { DiffEditor } from "@monaco-editor/react";
import { useTheme } from "next-themes";
import { toast } from "react-hot-toast";
import { useOrg } from "@/hooks/org/useOrg";
import { useDeploymentProgress } from "@/hooks/deployment";
import { useUserBranches, useDeployBranchFiles, useMergeBranch, useAbandonBranch } from "@/hooks/metadata_v1";
import type { BranchConflict } from "@/hooks/metadata_v1";
import { useActivityStore } from "@/stores/activityStore";
import { cn } from "@/lib/utils";
import { SuccessDeployments } from "./SuccessDeployments";
import { FailedDeployments } from "./FailedDeployments";
import { useResolvedGitTarget } from "@/hooks/git/useResolvedGitTarget";
import { useGitBranches } from "@/hooks/git/useGitBranches";
import { useSetDefaultGitBranch } from "@/hooks/git/useSetDefaultGitBranch";
import { useGitFile } from "@/hooks/git/useGitFile";
import type { GitBranch as GitBranchType } from "@/services/git/types";
import { useStagedFiles } from "@/hooks/git/useStagedFiles";
import { useStageFiles } from "@/hooks/git/useStageFiles";
import { useUnstageFiles } from "@/hooks/git/useUnstageFiles";
import { usePushOperation } from "@/hooks/git/usePushOperation";
import { useCommitStagedFiles } from "@/hooks/git/useCommitStagedFiles";

// ── Types ─────────────────────────────────────────────────────────────────────

type DeployTarget = "salesforce" | "github";

interface BranchFileItem {
  id: string;
  metadataItemId: string | null;
  name: string;
  type: string;
  path: string;
  lastModified: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const FILE_ICON: Record<string, React.ReactNode> = {
  ApexClass:             <FileCode className="h-3.5 w-3.5 text-blue-400" />,
  ApexTrigger:           <Zap className="h-3.5 w-3.5 text-purple-400" />,
  LightningComponentBundle: <Layout className="h-3.5 w-3.5 text-cyan-400" />,
  AuraDefinitionBundle:  <Layout className="h-3.5 w-3.5 text-cyan-400" />,
  Flow:                  <Workflow className="h-3.5 w-3.5 text-green-400" />,
  CustomObject:          <Database className="h-3.5 w-3.5 text-orange-400" />,
  ApexPage:              <FileText className="h-3.5 w-3.5 text-indigo-400" />,
  StaticResource:        <Archive className="h-3.5 w-3.5 text-zinc-400" />,
};
const getIcon = (type: string) => FILE_ICON[type] ?? <FileCode className="h-3.5 w-3.5 text-zinc-400" />;

// ── Root ──────────────────────────────────────────────────────────────────────

export function DeployLayout() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? "";

  // ── State ──
  const [target, setTarget] = useState<DeployTarget>(() => {
    const saved = localStorage.getItem("deploy:target");
    return (saved === "github" || saved === "salesforce") ? saved : "salesforce";
  });

  const handleTargetChange = (t: DeployTarget) => {
    localStorage.setItem("deploy:target", t);
    setTarget(t);
  };
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [commitMessage, setCommitMessage] = useState("");
  const [isDeployDialogOpen, setIsDeployDialogOpen] = useState(false);
  const [isDeploying, setIsDeploying]   = useState(false);
  const [conflict, setConflict]         = useState<BranchConflict | null>(null);
  const [pendingBranchIds, setPendingBranchIds] = useState<string[]>([]);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BranchFileItem | null>(null);
  const deployingCountRef = useRef(0);

  const isMetadataSyncing = useActivityStore((s) => s.isMetadataSyncing());

  // ── Data ──
  const { data, isLoading, error, refetch } = useUserBranches({ status: "ACTIVE" });
  const deployMutation  = useDeployBranchFiles();
  const mergeMutation   = useMergeBranch();
  const abandonMutation = useAbandonBranch(deleteTarget?.metadataItemId, {
    onSuccess: () => {
      toast.success(`Discarded changes to ${deleteTarget?.name ?? "file"}`, { position: "top-right" });
      setSelectedFiles((prev) => { const n = new Set(prev); n.delete(deleteTarget!.id); return n; });
      setDeleteTarget(null);
      refetch();
    },
    onError: (err) => toast.error(`Failed to discard: ${err.message}`, { duration: 5000, position: "top-right" }),
  });

  const { status: deployStatus, message: deployMessage, errorMessage: deployError,
    isComplete: deployComplete, connect: connectProgress, disconnect: disconnectProgress,
  } = useDeploymentProgress();

  const files = useMemo<BranchFileItem[]>(() => {
    if (!data) return [];
    return data.map((b) => ({
      id: b.id,
      metadataItemId: b.metadataItem?.id ?? null,
      name: b.fullName || b.fileName || "Unknown",
      type: b.metadataType,
      path: b.fileName ?? `${b.metadataType}/${b.fullName}`,
      lastModified: b.updatedAt,
    }));
  }, [data]);

  // ── Git staging ──
  const { mutate: stageFile }            = useStageFiles();
  const { mutate: unstageFile }          = useUnstageFiles();
  const { data: stagedFilesData }        = useStagedFiles({ enabled: !!orgId });
  const stagedRestoredRef                = useRef(false);

  // Restore selectedFiles from server on first load — reconcile staged paths → file IDs
  useEffect(() => {
    if (stagedRestoredRef.current) return;
    if (!files.length || !stagedFilesData) return;
    stagedRestoredRef.current = true;

    if (!stagedFilesData.length) return;
    const stagedPaths = new Set(stagedFilesData.map((s) => s.file_path));
    const restored = new Set(files.filter((f) => stagedPaths.has(f.path)).map((f) => f.id));
    if (restored.size > 0) setSelectedFiles(restored);
  }, [files, stagedFilesData]);

  // ── Handlers ──
  const handleSelectAll = (checked: boolean) => {
    setSelectedFiles(checked ? new Set(files.map((f) => f.id)) : new Set());
    if (checked) stageFile({ files: files.map((f) => ({ path: f.path, metadata_branch_id: f.id })) });
    else unstageFile({});
  };

  const handleToggleFile = (id: string) => {
    const file = files.find((f) => f.id === id);
    setSelectedFiles((prev) => {
      const n = new Set(prev);
      const isRemoving = n.has(id);
      isRemoving ? n.delete(id) : n.add(id);
      if (file) {
        isRemoving
          ? unstageFile({ paths: [file.path] })
          : stageFile({ files: [{ path: file.path, metadata_branch_id: file.id }] });
      }
      return n;
    });
  };

  const [sfDiffFileId, setSfDiffFileId] = useState<string | null>(null);
  const [ghDiffFileId, setGhDiffFileId] = useState<string | null>(null);

  const handleFileClick = (file: BranchFileItem, e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-no-nav]")) return;
    if (target === "salesforce") { setSfDiffFileId(file.id); return; }
    if (target === "github")     { setGhDiffFileId(file.id); return; }
    navigate(`/deployment/file/${file.metadataItemId}`);
  };

  const runDeploy = async (branchIds: string[]) => {
    deployingCountRef.current = branchIds.length;
    const result = await deployMutation.mutateAsync({
      organizationId: orgId,
      branchIds,
      commitMessage: commitMessage.trim() || undefined,
      deployOptions: { rollbackOnError: true },
    });
    if (result.conflict) { setConflict(result.conflict); setPendingBranchIds(branchIds); return; }
    if (result.deployment?.id) { setIsDeploying(true); connectProgress(result.deployment.id); }
  };

  const handleDeploy = async () => {
    try { await runDeploy(Array.from(selectedFiles)); }
    catch (err: any) { toast.error(`Deployment failed: ${err.message}`, { duration: 5000, position: "top-right" }); }
  };

  const handleResolveConflict = async (resolution: "USE_BRANCH" | "USE_MAIN") => {
    if (!conflict) return;
    const cb = data?.find((b) => b.metadataItem?.id === conflict.metadataItemId);
    if (!cb) return;
    setIsResolvingConflict(true);
    try {
      await mergeMutation.mutateAsync({ branchId: cb.id, resolvedConflict: { resolution } });
      const remaining = pendingBranchIds.filter((id) => id !== cb.id);
      setConflict(null);
      if (!remaining.length) {
        toast.success("All conflicts resolved.", { position: "top-right" });
        setIsDeployDialogOpen(false); setSelectedFiles(new Set()); refetch(); return;
      }
      setPendingBranchIds(remaining);
      await runDeploy(remaining);
    } catch (err: any) {
      toast.error(`Conflict resolution failed: ${err.message}`, { duration: 5000, position: "top-right" });
    } finally { setIsResolvingConflict(false); }
  };

  useEffect(() => {
    if (!deployComplete) return;
    if (deployStatus === "succeeded") {
      toast.success(`Deployed ${deployingCountRef.current} file(s)!`, { duration: 4000, position: "top-right" });
      setIsDeployDialogOpen(false); setSelectedFiles(new Set()); deployingCountRef.current = 0; refetch();
    } else if (deployStatus === "failed") {
      toast.error(`Deployment failed: ${deployError ?? "Unknown error"}`, { duration: 5000, position: "top-right" });
    }
    setIsDeploying(false); disconnectProgress();
  }, [deployComplete, deployStatus, deployError, refetch, disconnectProgress]);

  const selectedFilesData = useMemo(() => files.filter((f) => selectedFiles.has(f.id)), [files, selectedFiles]);
  const stagedFiles   = selectedFilesData;
  const unstagedFiles = useMemo(() => files.filter((f) => !selectedFiles.has(f.id)), [files, selectedFiles]);

  // ── Render ──
  return (
    <div className="flex h-full flex-col gap-3">
      {/* Target switcher */}
      <TargetSwitcher target={target} onChange={handleTargetChange} />

      {/* Main split pane */}
      <div className="flex flex-1 min-h-0 overflow-hidden rounded-xl border border-border bg-card">
        {/* Left — VSCode-style source control */}
        <div className="flex w-80 shrink-0 flex-col border-r border-border overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {isLoading && <LeftPanelSkeleton />}
            {error && (
              <div className="flex flex-col items-center justify-center h-32 gap-2 px-4 text-center">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-xs text-muted-foreground">{error.message}</span>
              </div>
            )}
            {!isLoading && !error && files.length === 0 && (
              <div className="flex flex-col items-center justify-center h-32 gap-2 px-4 text-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <span className="text-xs text-muted-foreground">No uncommitted changes</span>
              </div>
            )}
            {!isLoading && !error && files.length > 0 && (
              <>
                <VscFileSection
                  title="Staged Changes"
                  files={stagedFiles}
                  action="unstage"
                  onActionAll={() => handleSelectAll(false)}
                  onToggleFile={handleToggleFile}
                  onDiscard={(file) => setDeleteTarget(file)}
                  onFileClick={handleFileClick}
                />
                <VscFileSection
                  title="Changes"
                  files={unstagedFiles}
                  action="stage"
                  onActionAll={() => handleSelectAll(true)}
                  onToggleFile={handleToggleFile}
                  onDiscard={(file) => setDeleteTarget(file)}
                  onFileClick={handleFileClick}
                />
              </>
            )}
          </div>
        </div>

        {/* Right — action panel */}
        <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
          <ActionPanelTransition target={target}>
            {target === "salesforce" ? (
              <SalesforcePanel
                selectedFiles={selectedFilesData}
                commitMessage={commitMessage}
                onCommitMessageChange={setCommitMessage}
                onDeploy={() => { setPendingBranchIds(Array.from(selectedFiles)); setIsDeployDialogOpen(true); }}
                canDeploy={selectedFiles.size > 0 && !deployMutation.isPending && !isMetadataSyncing}
                isPending={deployMutation.isPending}
                diffFileId={sfDiffFileId}
                branches={data ?? []}
                onClearDiff={() => setSfDiffFileId(null)}
              />
            ) : (
              <GitHubPanel
                commitMessage={commitMessage}
                onCommitMessageChange={setCommitMessage}
                diffFileId={ghDiffFileId}
                branches={data ?? []}
                onClearDiff={() => setGhDiffFileId(null)}
                selectedFileIds={selectedFiles}
              />
            )}
          </ActionPanelTransition>
        </div>
      </div>


      {/* ── Dialogs ── */}
      <DeployDialog
        open={isDeployDialogOpen}
        onOpenChange={setIsDeployDialogOpen}
        conflict={conflict}
        isDeploying={isDeploying}
        deployMessage={deployMessage}
        deployingCount={deployingCountRef.current}
        selectedFiles={selectedFilesData}
        commitMessage={commitMessage}
        onCommitMessageChange={setCommitMessage}
        onConfirm={handleDeploy}
        onResolveConflict={handleResolveConflict}
        isResolvingConflict={isResolvingConflict}
        isPending={deployMutation.isPending}
        onClose={() => {
          if (isDeploying) disconnectProgress();
          setIsDeployDialogOpen(false); setIsDeploying(false); setCommitMessage(""); setConflict(null);
        }}
        theme={theme}
        deployStatus={deployStatus}
      />

      <DiscardDialog
        file={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && abandonMutation.mutate({ branchId: deleteTarget.id })}
        isPending={abandonMutation.isPending}
      />
    </div>
  );
}

// ── Target Switcher ───────────────────────────────────────────────────────────

function TargetSwitcher({ target, onChange }: { target: DeployTarget; onChange: (t: DeployTarget) => void }) {
  return (
    <Tabs value={target} onValueChange={(v) => onChange(v as DeployTarget)} className="w-full">
      <TabsList className="grid grid-cols-2 gap-2 h-auto w-full bg-transparent p-0">
        <TabsTrigger
          value="salesforce"
          className={cn(
            "flex items-center justify-start gap-3 px-4 py-3 rounded-xl border-2 h-auto transition-all",
            target === "salesforce"
              ? "border-blue-500 bg-blue-500/10 shadow-none"
              : "border-border bg-card opacity-60 hover:opacity-80 hover:bg-muted/30",
          )}
        >
          <div className={cn("p-2 rounded-lg shrink-0", target === "salesforce" ? "bg-blue-500/20" : "bg-muted/50")}>
            <Cloud className={cn("h-4 w-4", target === "salesforce" ? "text-blue-400" : "text-muted-foreground")} />
          </div>
          <div className="min-w-0 text-left">
            <p className={cn("text-sm font-semibold leading-none", target === "salesforce" ? "text-foreground" : "text-muted-foreground")}>
              Salesforce
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-none font-normal">Deploy to org directly</p>
          </div>
          <div className="ml-auto shrink-0">
            {target === "salesforce" ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/40 text-[10px] font-semibold text-blue-400">
                <Check className="h-2.5 w-2.5" /> Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/50 text-[10px] text-muted-foreground/50">
                Select
              </span>
            )}
          </div>
        </TabsTrigger>

        <TabsTrigger
          value="github"
          className={cn(
            "flex items-center justify-start gap-3 px-4 py-3 rounded-xl border-2 h-auto transition-all",
            target === "github"
              ? "border-violet-500 bg-violet-500/10 shadow-none"
              : "border-border bg-card opacity-60 hover:opacity-80 hover:bg-muted/30",
          )}
        >
          <div className={cn("p-2 rounded-lg shrink-0", target === "github" ? "bg-violet-500/20" : "bg-muted/50")}>
            <Github className={cn("h-4 w-4", target === "github" ? "text-violet-400" : "text-muted-foreground")} />
          </div>
          <div className="min-w-0 text-left">
            <p className={cn("text-sm font-semibold leading-none", target === "github" ? "text-foreground" : "text-muted-foreground")}>
              GitHub
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-none font-normal">Push to repository</p>
          </div>
          <div className="ml-auto shrink-0">
            {target === "github" ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/20 border border-violet-500/40 text-[10px] font-semibold text-violet-400">
                <Check className="h-2.5 w-2.5" /> Active
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/50 text-[10px] text-muted-foreground/50">
                Select
              </span>
            )}
          </div>
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

// ── Left Panel Skeleton ───────────────────────────────────────────────────────

function LeftPanelSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Section header */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-border/40 bg-card/95">
        <div className="h-3 w-3 rounded bg-muted shrink-0" />
        <div className="h-2.5 w-20 rounded bg-muted" />
        <div className="h-2 w-4 rounded bg-muted/50 ml-0.5" />
      </div>
      {[68, 52, 80, 44].map((w, i) => (
        <div key={i} className="flex items-center gap-2 pl-3 pr-2 h-10 border-b border-border/20 last:border-0">
          <div className="h-3.5 w-3.5 rounded-full border border-muted/60 bg-muted/30 shrink-0" />
          <div className="h-3.5 w-3.5 rounded bg-muted/50 shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="h-2.5 rounded bg-muted" style={{ width: `${w}%` }} />
            <div className="h-2 rounded bg-muted/40" style={{ width: `${Math.round(w * 0.55)}%` }} />
          </div>
          <div className="h-2.5 w-3 rounded bg-muted/40 shrink-0" />
        </div>
      ))}
    </div>
  );
}

// ── VSCode-style File Section ─────────────────────────────────────────────────

function VscFileSection({
  title, files, action, onActionAll, onToggleFile, onDiscard, onFileClick,
}: {
  title: string;
  files: BranchFileItem[];
  action: "stage" | "unstage";
  onActionAll: () => void;
  onToggleFile: (id: string) => void;
  onDiscard: (file: BranchFileItem) => void;
  onFileClick: (file: BranchFileItem, e: React.MouseEvent) => void;
}) {
  const [open, setOpen] = useState(true);

  const chevronSpring = useSpring({
    rotate: open ? 90 : 0,
    config: { tension: 320, friction: 24 },
  });

  const transitions = useTransition(open ? files : [], {
    keys: (f) => f.id,
    from:  { opacity: 0, height: 0 },
    enter: { opacity: 1, height: 40 },
    leave: { opacity: 0, height: 0 },
    config: { tension: 340, friction: 30 },
  });

  if (files.length === 0) return null;

  return (
    <div>
      {/* Section header */}
      <div className="group flex items-center gap-1 px-1 py-1 select-none sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border/40">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 flex-1 min-w-0 px-1.5 py-0.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
        >
          <animated.span
            style={{ display: "inline-flex", rotate: chevronSpring.rotate.to((r) => `${r}deg`) }}
          >
            <ChevronRight className="h-3 w-3 shrink-0" />
          </animated.span>
          <span className="text-[11px] font-semibold uppercase tracking-wider">{title}</span>
          <span className="text-[10px] text-muted-foreground/70 ml-0.5">{files.length}</span>
        </button>

        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pr-1">
          <button
            type="button"
            onClick={onActionAll}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={action === "stage" ? "Stage all changes" : "Unstage all changes"}
          >
            {action === "stage" ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* Animated file rows */}
      {transitions((style, file) => (
        <animated.div style={{ overflow: "hidden", ...style }}>
          <VscFileRow
            file={file}
            action={action}
            onToggle={() => onToggleFile(file.id)}
            onDiscard={(e) => { e.stopPropagation(); onDiscard(file); }}
            onClick={(e) => onFileClick(file, e)}
          />
        </animated.div>
      ))}
    </div>
  );
}

// ── Stage Indicator ───────────────────────────────────────────────────────────

function StageIndicator({ checked, onToggle }: { checked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      data-no-nav
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={cn(
        "shrink-0 flex items-center justify-center rounded-full border transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "h-3.5 w-3.5",
        checked
          ? "bg-primary border-primary text-primary-foreground"
          : "border-muted-foreground/30 hover:border-muted-foreground/60 bg-transparent",
      )}
    >
      {checked && <Check className="h-2 w-2 stroke-[3]" />}
    </button>
  );
}

// ── VSCode-style File Row ─────────────────────────────────────────────────────

function VscFileRow({ file, action, onToggle, onDiscard, onClick }: {
  file: BranchFileItem;
  action: "stage" | "unstage";
  onToggle: () => void;
  onDiscard: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isStaged = action === "unstage";

  const badgeSpring = useSpring({
    opacity: hovered ? 0 : 1,
    x: hovered ? 6 : 0,
    config: { tension: 380, friction: 26 },
  });

  const actionsSpring = useSpring({
    opacity: hovered ? 1 : 0,
    x: hovered ? 0 : 8,
    config: { tension: 380, friction: 26 },
  });

  const rowSpring = useSpring({
    backgroundColor: hovered ? "hsl(var(--muted) / 0.4)" : "hsl(var(--muted) / 0)",
    config: { tension: 300, friction: 28 },
  });

  return (
    <animated.div
      style={rowSpring}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      className="flex items-center gap-2 pl-3 pr-2 h-10 cursor-pointer"
    >
      <StageIndicator checked={isStaged} onToggle={onToggle} />
      <div className="shrink-0">{getIcon(file.type)}</div>

      <div className="flex-1 min-w-0">
        <span className="text-xs text-foreground truncate block">{file.name}</span>
        <span className="text-[10px] text-muted-foreground/60 truncate block">{file.type}</span>
      </div>

      {/* Right slot: M badge slides out, action buttons slide in */}
      <div className="relative flex items-center justify-end shrink-0 w-16 h-full overflow-hidden" data-no-nav>
        <animated.span
          style={{
            opacity: badgeSpring.opacity,
            transform: badgeSpring.x.to((x) => `translateX(${x}px)`),
          }}
          className="absolute right-1 text-[10px] font-mono text-amber-400/70 pointer-events-none select-none"
        >
          M
        </animated.span>

        <animated.div
          style={{
            opacity: actionsSpring.opacity,
            transform: actionsSpring.x.to((x) => `translateX(${x}px)`),
            pointerEvents: hovered ? "auto" : "none",
          }}
          className="absolute right-0 flex items-center gap-0.5"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDiscard(e); }}
            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Discard changes"
          >
            <Trash2 className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title={action === "stage" ? "Stage changes" : "Unstage changes"}
          >
            {action === "stage" ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </button>
        </animated.div>
      </div>
    </animated.div>
  );
}

// ── Right panel: animated transition between Salesforce and GitHub ─────────────

function ActionPanelTransition({ target, children }: { target: DeployTarget; children: React.ReactNode }) {
  const transitions = useTransition(target, {
    from:    { opacity: 0, transform: "translateY(6px)" },
    enter:   { opacity: 1, transform: "translateY(0px)" },
    leave:   { opacity: 0, transform: "translateY(-6px)" },
    config:  { tension: 280, friction: 24 },
  });
  return (
    <div className="relative flex-1 h-full overflow-hidden">
      {transitions((style, t) =>
        t === target ? (
          <animated.div style={{ ...style, position: "absolute", inset: 0, overflow: "auto" }}>
            {children}
          </animated.div>
        ) : null
      )}
    </div>
  );
}

// ── Salesforce Panel ──────────────────────────────────────────────────────────

type SfView = "deploy" | "history" | "diff";
type HistoryTab = "success" | "failed";

function SalesforcePanel({ selectedFiles, commitMessage, onCommitMessageChange, onDeploy, canDeploy, isPending, diffFileId, branches, onClearDiff }: {
  selectedFiles: BranchFileItem[];
  commitMessage: string;
  onCommitMessageChange: (v: string) => void;
  onDeploy: () => void;
  canDeploy: boolean;
  isPending: boolean;
  diffFileId: string | null;
  branches: any[];
  onClearDiff: () => void;
}) {
  const [view, setView]         = useState<SfView>("deploy");
  const [historyTab, setHistoryTab] = useState<HistoryTab>("success");

  // Auto-switch to diff tab when a file is clicked
  useEffect(() => {
    if (diffFileId) setView("diff");
  }, [diffFileId]);

  const btnSpring = useSpring({
    scale: canDeploy ? 1 : 0.97,
    opacity: canDeploy ? 1 : 0.5,
    config: { tension: 320, friction: 26 },
  });

  const viewTransitions = useTransition(view, {
    keys: (v) => v,
    from:  { opacity: 0, x: view === "deploy" ? -12 : 12 },
    enter: { opacity: 1, x: 0 },
    leave: { opacity: 0, x: view === "deploy" ? 12 : -12 },
    config: { tension: 300, friction: 28 },
  });

  return (
    <div className="flex flex-col h-full">
      {/* ── Top nav: Deploy | History | Diff ── */}
      <div className="flex items-center gap-1 border-b border-border pb-0 shrink-0">
        {(["deploy", "history", "diff"] as const).map((v) => {
          if (v === "diff" && !diffFileId) return null;
          return (
          <button
            key={v}
            type="button"
            onClick={() => { if (v !== "diff") onClearDiff(); setView(v); }}
            className={cn(
              "relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors rounded-t-md",
              view === v
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {v === "deploy" ? <Upload className="h-3 w-3" />
              : v === "history" ? <CheckCircle2 className="h-3 w-3" />
              : <GitMerge className="h-3 w-3" />}
            {v === "deploy" ? "Deploy" : v === "history" ? "History" : "Diff"}
            {/* Active underline */}
            {view === v && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-primary" />
            )}
          </button>
          );
        })}
      </div>

      {/* ── Animated view body ── */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {viewTransitions((style, v) =>
          v === view ? (
            <animated.div
              style={{
                ...style,
                transform: (style as any).x.to((x: number) => `translateX(${x}px)`),
                position: "absolute",
                inset: 0,
                overflow: "auto",
              }}
            >
              {v === "deploy" ? (
                <SalesforceDeployForm
                  selectedFiles={selectedFiles}
                  commitMessage={commitMessage}
                  onCommitMessageChange={onCommitMessageChange}
                  onDeploy={onDeploy}
                  canDeploy={canDeploy}
                  isPending={isPending}
                  btnSpring={btnSpring}
                />
              ) : v === "diff" ? (
                <SalesforceDiffView
                  fileId={diffFileId}
                  branches={branches}
                  onClose={() => { onClearDiff(); setView("deploy"); }}
                />
              ) : (
                <SalesforceHistory tab={historyTab} onTabChange={setHistoryTab} />
              )}
            </animated.div>
          ) : null
        )}
      </div>
    </div>
  );
}

// ── Salesforce Diff View ──────────────────────────────────────────────────────

function SalesforceDiffView({ fileId, branches, onClose }: {
  fileId: string | null;
  branches: any[];
  onClose: () => void;
}) {
  const { theme } = useTheme();
  const branch = branches.find((b) => b.id === fileId);

  if (!branch) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <AlertCircle className="h-5 w-5" />
        <span className="text-xs">File not found</span>
      </div>
    );
  }

  const fileName  = branch.fullName;
  const fileType  = branch.metadataType;
  const original  = branch.metadataItem?.content ?? "";
  const modified  = branch.content ?? "";

  return (
    <div className="flex flex-col h-full">
      {/* File header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border shrink-0">
        {getIcon(fileType)}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{fileName}</p>
          <p className="text-[10px] text-muted-foreground">{fileType}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Close diff"
        >
          <XCircle className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Labels */}
      <div className="flex border-b border-border shrink-0">
        <div className="flex-1 px-4 py-1.5 flex items-center gap-1.5 border-r border-border">
          <span className="h-2 w-2 rounded-full bg-sky-400 shrink-0" />
          <span className="text-[10px] text-muted-foreground">Salesforce (original)</span>
        </div>
        <div className="flex-1 px-4 py-1.5 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
          <span className="text-[10px] text-muted-foreground">Local (modified)</span>
        </div>
      </div>

      {/* Monaco inline diff */}
      <div className="flex-1 min-h-0">
        <DiffEditor
          height="100%"
          original={original}
          modified={modified}
          language="apex"
          theme={theme === "dark" ? "vs-dark" : "vs"}
          options={{
            readOnly: true,
            renderSideBySide: false,
            minimap: { enabled: false },
            fontSize: 12,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            lineNumbers: "on",
            folding: true,
          }}
        />
      </div>
    </div>
  );
}

// ── Salesforce Deploy Form (extracted inner view) ─────────────────────────────

function SalesforceDeployForm({ selectedFiles, commitMessage, onCommitMessageChange, onDeploy, canDeploy, isPending, btnSpring }: {
  selectedFiles: BranchFileItem[];
  commitMessage: string;
  onCommitMessageChange: (v: string) => void;
  onDeploy: () => void;
  canDeploy: boolean;
  isPending: boolean;
  btnSpring: any;
}) {
  return (
    <div className="flex flex-col h-full p-5 gap-4">
      {/* Header */}
      <div className="flex items-center gap-2 shrink-0">
        <Cloud className="h-4 w-4 text-blue-400" />
        <h3 className="text-sm font-semibold text-foreground">Deploy to Salesforce</h3>
      </div>

      {/* Commit message */}
      <div className="space-y-1.5 shrink-0">
        <label className="text-xs font-medium text-muted-foreground">Commit message (optional)</label>
        <textarea
          value={commitMessage}
          onChange={(e) => onCommitMessageChange(e.target.value)}
          placeholder="Describe the changes you're deploying…"
          rows={3}
          className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
        />
      </div>

      {/* Files — absorbs remaining space so button stays pinned at bottom */}
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        <div className="flex items-center justify-between shrink-0">
          <label className="text-xs font-medium text-muted-foreground">Files to deploy</label>
          {selectedFiles.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""}</span>
          )}
        </div>
        {selectedFiles.length === 0 ? (
          <p className="text-xs text-muted-foreground/50 py-1">Stage files on the left to deploy</p>
        ) : (
          <div className="flex-1 overflow-y-auto divide-y divide-border/40">
            {selectedFiles.map((f) => (
              <div key={f.id} className="flex items-center gap-2.5 py-2">
                {getIcon(f.type)}
                <span className="text-xs text-foreground truncate flex-1">{f.name}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">{f.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notice */}
      <div className="flex items-start gap-2 rounded-lg bg-blue-500/5 border border-blue-500/15 px-3 py-2.5 shrink-0">
        <AlertCircle className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Conflicts will be detected before deployment starts. Rollback on error is enabled.
        </p>
      </div>

      {/* Deploy — full-width, always at bottom */}
      <animated.button
        type="button"
        style={btnSpring}
        onClick={onDeploy}
        disabled={!canDeploy}
        className="shrink-0 flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:pointer-events-none transition-colors hover:bg-primary/90"
      >
        {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {isPending ? "Checking…" : `Deploy${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""}`}
      </animated.button>
    </div>
  );
}

// ── Salesforce History View ───────────────────────────────────────────────────

function SalesforceHistory({ tab, onTabChange }: { tab: HistoryTab; onTabChange: (t: HistoryTab) => void }) {
  return (
    <div className="flex flex-col h-full">
      {/* Sub-tabs: Successful | Failed */}
      <div className="flex items-center gap-0.5 px-5 py-3 border-b border-border shrink-0">
        {(["success", "failed"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTabChange(t)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
              tab === t
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
          >
            {t === "success"
              ? <CheckCircle2 className="h-3 w-3 text-emerald-400" />
              : <XCircle className="h-3 w-3 text-rose-400" />}
            {t === "success" ? "Successful" : "Failed"}
          </button>
        ))}
      </div>

      {/* Actual deployment history — scroll within the bounded panel */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {tab === "success" ? <SuccessDeployments /> : <FailedDeployments />}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SFDX_DIR: Record<string, string> = {
  ApexClass: "classes", ApexTrigger: "triggers", LightningComponentBundle: "lwc",
  AuraDefinitionBundle: "aura", Flow: "flows", CustomObject: "objects",
  ApexPage: "pages", ApexComponent: "components", StaticResource: "staticresources",
  PermissionSet: "permissionsets", Profile: "profiles", Layout: "layouts",
};

function buildSfdxPath(metadataType: string, fileName: string) {
  const dir = SFDX_DIR[metadataType] ?? metadataType.toLowerCase();
  return `force-app/main/default/${dir}/${fileName}`;
}

// ── GitHub Panel ──────────────────────────────────────────────────────────────

type GhView = "push" | "diff";

function GitHubPanel({ commitMessage, onCommitMessageChange, diffFileId, branches, onClearDiff, selectedFileIds }: {
  commitMessage: string;
  onCommitMessageChange: (v: string) => void;
  diffFileId: string | null;
  branches: any[];
  onClearDiff: () => void;
  selectedFileIds: Set<string>;
}) {
  const [view, setView] = useState<GhView>("push");

  useEffect(() => {
    if (diffFileId) setView("diff");
  }, [diffFileId]);
  // Resolution (connection -> selected repo -> default branch) lives in `useResolvedGitTarget`
  // (L-21) — shared with the changeset pane's save->stage->commit chain so the two surfaces
  // never grow separate copies of "which repo/branch does this org commit to".
  const { isConnected, repo, branch, isLoading } = useResolvedGitTarget();
  const { data: allStagedFiles = [], isLoading: loadingStaged } = useStagedFiles({ enabled: isConnected });

  // Only include server-staged files that the user has actually checked on the left panel
  const stagedFiles = allStagedFiles.filter(
    (f) => f.metadata_branch_id && selectedFileIds.has(f.metadata_branch_id),
  );

  const queryClient = useQueryClient();
  const [pushOpId, setPushOpId] = useState<string | null>(null);
  const { data: pushOp } = usePushOperation(pushOpId);
  const { mutate: commitFiles, isPending: isCommitting } = useCommitStagedFiles();


  const canPush =
    isConnected && !!repo && !!branch &&
    stagedFiles.length > 0 &&
    stagedFiles.every((f) => f.metadata_branch_id) &&
    !isCommitting && pushOp?.status !== "in_progress";

  useEffect(() => {
    if (!pushOp) return;
    if (pushOp.status === "completed") {
      toast.success(`Pushed ${pushOp.files_pushed} file(s) to ${pushOp.branch_name}`, { position: "top-right" });
      queryClient.invalidateQueries({ queryKey: ['stagedFiles'] });
      queryClient.invalidateQueries({ queryKey: ['userBranches'] });
      setPushOpId(null);
    }
    if (pushOp.status === "failed") {
      toast.error(`Push failed: ${pushOp.error ?? "Unknown error"}`, { duration: 5000, position: "top-right" });
      setPushOpId(null);
    }
  }, [pushOp?.status]);

  const handlePush = () => {
    if (!repo || !branch) return;
    commitFiles(
      {
        repo_full_name: repo.full_name,
        branch: branch.name,
        commit_message: commitMessage.trim() || "chore: push metadata to GitHub",
        staged_file_ids: stagedFiles.map((f) => f.id),
      },
      {
        onSuccess: (d) => setPushOpId(d.push_operation_id),
        onError: (e) => toast.error(`Push failed: ${e.message}`, { duration: 5000, position: "top-right" }),
      },
    );
  };

  // ── Not connected ──
  if (!isLoading && !isConnected) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-3 p-8 text-center">
        <div className="p-3 rounded-xl bg-muted/50 border border-border">
          <Github className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs font-medium text-foreground">GitHub not connected</p>
          <p className="text-[11px] text-muted-foreground mt-1">Connect your GitHub account to push metadata.</p>
        </div>
        <a href="/studio/git" className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">
          <Github className="h-3 w-3" /> Connect GitHub
        </a>
      </div>
    );
  }

  // ── No repo selected ──
  if (!isLoading && isConnected && !repo) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-3 p-8 text-center">
        <div className="p-3 rounded-xl bg-muted/50 border border-border">
          <GitBranch className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs font-medium text-foreground">No repository linked</p>
          <p className="text-[11px] text-muted-foreground mt-1">Select a repo in the Git module first.</p>
        </div>
        <a href="/studio/git" className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors">
          Go to Git module
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar: Push | Diff */}
      <div className="flex items-center gap-1 border-b border-border pb-0 shrink-0">
        {(["push", "diff"] as const).map((v) => {
          if (v === "diff" && !diffFileId) return null;
          return (
            <button
              key={v}
              type="button"
              onClick={() => { if (v !== "diff") onClearDiff(); setView(v); }}
              className={cn(
                "relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors",
                view === v ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {v === "push" ? <Upload className="h-3 w-3" /> : <GitMerge className="h-3 w-3" />}
              {v === "push" ? "Push" : "Diff"}
              {view === v && <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-primary" />}
            </button>
          );
        })}
      </div>

      {/* View body */}
      {view === "diff" && diffFileId ? (
        <GitHubDiffView
          fileId={diffFileId}
          branches={branches}
          repo={repo}
          gitRef={branch?.name ?? repo?.default_branch}
          onClose={() => { onClearDiff(); setView("push"); }}
        />
      ) : (
    <div className="flex flex-col h-full p-5 gap-4">
      {/* Header */}
      <div className="flex items-center gap-2 shrink-0">
        <Github className="h-4 w-4 text-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Push to GitHub</h3>
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />}
      </div>

      {/* Repo pill */}
      {repo && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border/60">
          <Github className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-xs text-foreground truncate">{repo.full_name}</span>
        </div>
      )}

      {/* Branch selector */}
      {repo && (
        <GitBranchDropdown repoFullName={repo.full_name} workingBranchId={branch?.id ?? null} />
      )}

      {/* Commit message */}
      <div className="space-y-1.5 shrink-0">
        <label className="text-xs font-medium text-muted-foreground">Commit message (optional)</label>
        <textarea
          value={commitMessage}
          onChange={(e) => onCommitMessageChange(e.target.value)}
          placeholder="feat: update Salesforce metadata"
          rows={3}
          className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
        />
      </div>

      {/* Files — absorbs remaining space so button stays pinned */}
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        <div className="flex items-center justify-between shrink-0">
          <label className="text-xs font-medium text-muted-foreground">Files to push</label>
          {stagedFiles.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{stagedFiles.length} file{stagedFiles.length !== 1 ? "s" : ""}</span>
          )}
        </div>
        {loadingStaged ? (
          <div className="flex items-center gap-2 py-1 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /><span className="text-xs">Loading…</span>
          </div>
        ) : stagedFiles.length === 0 ? (
          <p className="text-xs text-muted-foreground/50 py-1">Stage files on the left to push</p>
        ) : (
          <div className="flex-1 overflow-y-auto divide-y divide-border/40">
            {stagedFiles.map((f) => (
              <div key={f.file_path} className="flex items-center gap-2.5 py-2">
                {getIcon(f.file_path.split("/").pop()?.split(".")[0] ?? "")}
                <span className="text-xs text-foreground truncate flex-1">{f.file_path.split("/").pop()}</span>
                <span className="text-[10px] text-muted-foreground shrink-0 font-mono">
                  {new Date(f.staged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Push progress (replaces notice when active) */}
      {pushOp && (pushOp.status === "pending" || pushOp.status === "in_progress") ? (
        <div className="shrink-0 flex items-center gap-2 rounded-lg bg-blue-500/5 border border-blue-500/15 px-3 py-2.5">
          <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Pushing {pushOp.files_pushed ?? 0} / {pushOp.files_total} files…
          </p>
        </div>
      ) : (
        <div className="shrink-0 flex items-start gap-2 rounded-lg bg-blue-500/5 border border-blue-500/15 px-3 py-2.5">
          <AlertCircle className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Conflicts will be detected before deployment starts. Rollback on error is enabled.
          </p>
        </div>
      )}

      {/* Push — full-width, always at bottom */}
      <button
        type="button"
        onClick={handlePush}
        disabled={!canPush}
        className="shrink-0 flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none transition-colors hover:bg-primary/90"
      >
        {isCommitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {isCommitting ? "Pushing…" : `Push${stagedFiles.length > 0 ? ` (${stagedFiles.length})` : ""}`}
      </button>
    </div>
      )}
    </div>
  );
}

// ── Git Branch Dropdown ───────────────────────────────────────────────────────

function GitBranchDropdown({ repoFullName, workingBranchId }: {
  repoFullName: string;
  workingBranchId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  const { data: branches = [], isLoading } = useGitBranches(repoFullName);
  const { mutate: setDefault, isPending, variables } = useSetDefaultGitBranch();

  const workingBranch = branches.find((b) => b.id === workingBranchId) ?? null;

  useEffect(() => {
    if (listRef.current) setMeasuredHeight(listRef.current.scrollHeight);
  }, [branches, open]);

  const listSpring = useSpring({
    height: open ? measuredHeight : 0,
    opacity: open ? 1 : 0,
    config: { tension: 280, friction: 26, clamp: true },
  });

  const chevronSpring = useSpring({
    rotate: open ? 180 : 0,
    config: { tension: 280, friction: 26 },
  });

  function handleSelect(branch: GitBranchType) {
    if (branch.id === null) return;
    const isClearing = branch.id === workingBranchId;
    setDefault({ gitBranchId: isClearing ? null : branch.id });
    setOpen(false);
  }

  return (
    <div className="shrink-0 space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">Branch</label>
      <div className="rounded-lg border border-border overflow-hidden">
        {/* Trigger */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 w-full px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors"
        >
          <GitBranch className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className={cn(
            "text-xs flex-1 text-left truncate font-mono",
            workingBranch ? "text-foreground" : "text-muted-foreground italic",
          )}>
            {isLoading ? "Loading…" : workingBranch?.name ?? "No branch selected"}
          </span>
          {isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-muted-foreground" />
            : (
              <animated.div style={{ rotate: chevronSpring.rotate }}>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </animated.div>
            )
          }
        </button>

        {/* Animated list */}
        <animated.div style={{ ...listSpring, overflow: "hidden" }} className="border-t border-border">
          <div ref={listRef} className="max-h-44 overflow-y-auto">
            {branches.length === 0 && !isLoading && (
              <p className="px-3 py-2.5 text-xs text-muted-foreground">No branches found.</p>
            )}
            {branches.map((b) => {
              const isWorking = b.id !== null && b.id === workingBranchId;
              const isSettingThis = isPending && (variables?.gitBranchId === b.id || (isWorking && variables?.gitBranchId === null));
              return (
                <button
                  key={b.name}
                  type="button"
                  disabled={b.id === null || isPending}
                  onClick={() => handleSelect(b)}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-3 py-2 text-xs transition-colors border-b border-border/40 last:border-0",
                    isWorking
                      ? "bg-emerald-500/8 text-emerald-400"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
                    b.id === null && "opacity-40 cursor-not-allowed",
                  )}
                >
                  <GitBranch className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-mono truncate flex-1 text-left">{b.name}</span>
                  <span className="text-[10px] text-muted-foreground/60 font-mono shrink-0">{b.sha.slice(0, 7)}</span>
                  {isSettingThis
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 ml-1" />
                    : isWorking && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 ml-1" />
                  }
                </button>
              );
            })}
          </div>
        </animated.div>
      </div>
    </div>
  );
}

// ── GitHub Diff View ──────────────────────────────────────────────────────────

function GitHubDiffView({ fileId, branches, repo, gitRef, onClose }: {
  fileId: string | null;
  branches: any[];
  repo: any;
  gitRef: string | undefined;
  onClose: () => void;
}) {
  const { theme } = useTheme();
  const branch = branches.find((b) => b.id === fileId);

  const githubPath = branch
    ? buildSfdxPath(branch.metadataType, branch.fileName ?? branch.fullName)
    : null;

  const { data: githubFile, isLoading } = useGitFile(
    repo?.full_name ?? null,
    githubPath,
    gitRef,
  );

  if (!branch) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <AlertCircle className="h-5 w-5" />
        <span className="text-xs">File not found</span>
      </div>
    );
  }

  const localContent  = branch.content ?? "";
  const remoteContent = githubFile?.content ?? "";

  return (
    <div className="flex flex-col h-full">
      {/* File header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border shrink-0">
        {getIcon(branch.metadataType)}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">{branch.fullName}</p>
          <p className="text-[10px] text-muted-foreground">{branch.metadataType}</p>
        </div>
        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Close diff"
        >
          <XCircle className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Labels */}
      <div className="flex border-b border-border shrink-0">
        <div className="flex-1 px-4 py-1.5 flex items-center gap-1.5 border-r border-border">
          <Github className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-[10px] text-muted-foreground truncate">
            {gitRef ?? "GitHub"}
            {isLoading && " · loading…"}
            {!isLoading && !githubFile && " · not found"}
          </span>
        </div>
        <div className="flex-1 px-4 py-1.5 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
          <span className="text-[10px] text-muted-foreground">Local (staged)</span>
        </div>
      </div>

      {/* Loading state / Diff editor */}
      <div className="flex-1 min-h-0 relative">
        {isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background">
            <div className="flex items-center gap-2.5 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span className="text-xs">Fetching file from GitHub…</span>
            </div>
            <p className="text-[11px] text-muted-foreground/60">
              Comparing against{" "}
              <span className="font-mono">{gitRef ?? "default branch"}</span>
            </p>
          </div>
        ) : (
          <DiffEditor
            height="100%"
            original={remoteContent}
            modified={localContent}
            language="apex"
            theme={theme === "dark" ? "vs-dark" : "vs"}
            options={{
              readOnly: true,
              renderSideBySide: false,
              minimap: { enabled: false },
              fontSize: 12,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        )}
      </div>
    </div>
  );
}


// ── Deploy Dialog ─────────────────────────────────────────────────────────────

function DeployDialog({
  open, onOpenChange, conflict, isDeploying, deployMessage, deployingCount,
  selectedFiles, commitMessage, onCommitMessageChange, onConfirm, onResolveConflict,
  isResolvingConflict, isPending, onClose, theme, deployStatus,
}: any) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              {conflict
                ? <AlertCircle className="h-5 w-5 text-orange-400" />
                : <Upload className="h-5 w-5 text-primary" />}
            </div>
            <div>
              <div className="text-base font-semibold">
                {conflict ? "Conflict Detected" : "Deploy to Salesforce"}
              </div>
              <div className="text-xs font-normal text-muted-foreground mt-0.5">
                {conflict ? `${conflict.fullName} — choose how to resolve` : `${selectedFiles.length} file(s) selected`}
              </div>
            </div>
          </DialogTitle>
          {!conflict && <DialogDescription>Review the files you're about to deploy</DialogDescription>}
        </DialogHeader>

        <DialogMain className="space-y-4">
          {conflict && (
            <div className="space-y-3">
              <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-xs text-orange-300">
                <strong>{conflict.fullName}</strong> changed on main since your branch was created. Choose which version to keep.
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="flex bg-muted/50 border-b border-border text-xs">
                  <div className="flex-1 px-4 py-2 border-r border-border flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400" /> Main (current)
                  </div>
                  <div className="flex-1 px-4 py-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" /> Your branch
                  </div>
                </div>
                <DiffEditor
                  height="260px"
                  original={conflict.mainContent}
                  modified={conflict.branchContent}
                  language="apex"
                  theme={theme === "dark" ? "vs-dark" : "vs"}
                  options={{ readOnly: true, renderSideBySide: true, minimap: { enabled: false }, fontSize: 12, scrollBeyondLastLine: false, automaticLayout: true }}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onResolveConflict("USE_MAIN")}
                  disabled={isResolvingConflict}
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                >
                  {isResolvingConflict && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Use Main
                </button>
                <button
                  type="button"
                  onClick={() => onResolveConflict("USE_BRANCH")}
                  disabled={isResolvingConflict}
                  className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isResolvingConflict && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  <GitMerge className="h-3.5 w-3.5" /> Keep Mine
                </button>
              </div>
            </div>
          )}

          {!conflict && isDeploying && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg text-xs">
                <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                {deployMessage || `Deploying ${deployingCount} file(s)…`}
              </div>
              <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full animate-progress-indeterminate" />
              </div>
            </div>
          )}

          {!conflict && !isDeploying && (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Commit message</label>
                <textarea
                  value={commitMessage}
                  onChange={(e) => onCommitMessageChange(e.target.value)}
                  placeholder="Describe the changes you're deploying…"
                  rows={2}
                  className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>
              <div className="border border-border rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                <div className="bg-muted/50 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground sticky top-0">
                  Files ({selectedFiles.length})
                </div>
                {selectedFiles.map((f: BranchFileItem) => (
                  <div key={f.id} className="flex items-center gap-2.5 px-3 py-2 border-b border-border/50 last:border-0">
                    {getIcon(f.type)}
                    <span className="text-xs text-foreground flex-1 truncate">{f.name}</span>
                    <span className="text-[10px] text-muted-foreground">{f.type}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogMain>

        <DialogFooter className="flex gap-2">
          {!conflict && (
            <button
              type="button"
              onClick={onClose}
              disabled={isDeploying && deployStatus === "in_progress"}
              className="px-4 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
            >
              {isDeploying ? "Close" : "Cancel"}
            </button>
          )}
          {!conflict && !isDeploying && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPending}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {isPending ? "Checking…" : "Deploy Now"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Discard Dialog ────────────────────────────────────────────────────────────

function DiscardDialog({ file, onClose, onConfirm, isPending }: {
  file: BranchFileItem | null;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o && !isPending) onClose(); }}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <span className="text-sm font-semibold">Discard Changes?</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            This will revert <strong className="text-foreground">{file?.name}</strong> to the main version. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {isPending ? "Discarding…" : "Discard"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
